import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Crown,
  Download,
  Gem,
  Heart,
  Rewind as RewindIcon,
  Search,
  Sparkles,
  Star,
} from 'lucide-react'
import {
  type VIPOrder,
  type VIPSku,
  type VIPTierName,
  ApiError,
  createVIPOrder,
  fetchVIPOrder,
  fetchVIPSkus,
  qrisDownloadURL,
} from '../lib/api'
import { downloadFile, openExternalLink, useTelegramBackButton } from '../lib/telegram'
import { hapticNotification } from '../lib/haptics'
import { clearPendingOrder, getPendingOrder, savePendingOrder } from '../lib/pendingOrder'

const POLL_INTERVAL_MS = 3000
// Poll rate while the tab/WebView is hidden — slower than POLL_INTERVAL_MS
// but never zero. The old behavior stopped polling entirely while hidden,
// which meant an order paid while the user was in their banking app (the
// whole point of leaving the Mini App to scan a QR) was never detected
// until they manually returned and the visibilitychange listener fired.
const BACKGROUND_POLL_INTERVAL_MS = 10000

// Every tier that could ever appear, in ladder order — used to render the
// tier switcher deterministically. A tier the backend doesn't currently
// sell (e.g. Supreme, withheld unconditionally — see visibleVIPSKUs in
// apps/api/vip_handlers.go) simply never has a matching SKU, so its pill
// never renders (see availableTiers below); nothing here assumes Supreme is
// actually purchasable.
const TIER_ORDER: VIPTierName[] = ['vip', 'premium', 'supreme']

type PageState =
  | { status: 'loading' }
  | { status: 'selecting'; skus: VIPSku[]; tier: VIPTierName; selected: number }
  | { status: 'creating' }
  | { status: 'awaiting_payment'; order: VIPOrder }
  | { status: 'completed'; order: VIPOrder }
  // lastSku is set from the expired/failed order's own tier+duration_days
  // (when present — see VIPOrder.duration_days' doc comment) so the expired
  // screen can regenerate the same SKU in one tap instead of sending the
  // buyer back through the tier picker from scratch.
  | { status: 'expired'; lastSku: { tier: VIPTierName; durationDays: number } | null }
  | { status: 'error'; message: string }

function formatIDR(amount: number): string {
  return 'Rp' + Math.round(amount).toLocaleString('id-ID')
}

function formatCountdown(msRemaining: number): string {
  const totalSeconds = Math.max(0, Math.floor(msRemaining / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

// lastSkuFrom extracts the regenerate-same-SKU payload from an
// expired/failed order — null if duration_days is missing (an order resumed
// from a stale localStorage entry predating that field; see
// VIPOrder.duration_days' doc comment), in which case the expired screen
// falls back to the normal tier picker.
function lastSkuFrom(order: VIPOrder): { tier: VIPTierName; durationDays: number } | null {
  return order.duration_days != null
    ? { tier: order.tier, durationDays: order.duration_days }
    : null
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

const DURATION_LABEL_KEYS: Record<number, string> = {
  7: 'vip.durationWeek',
  30: 'vip.durationMonth1',
  90: 'vip.durationMonth3',
  365: 'vip.durationMonth12',
}

// Every duration's "per week" rate is normalized against the SAME tier's
// own 1-week SKU (the anchor is per tier, not global — Premium's 7-day
// price is a different anchor than VIP's) — matches
// docs/ui-references/omi/vip/tier-purchase/upsell-01.jpeg's anchoring trick.
function perWeekPrice(sku: VIPSku): number {
  return (sku.price_idr / sku.duration_days) * 7
}

function savePercent(sku: VIPSku, baselinePerWeek: number): number {
  const perWeek = perWeekPrice(sku)
  if (perWeek >= baselinePerWeek) return 0
  return Math.round((1 - perWeek / baselinePerWeek) * 100)
}

// Visual theme per tier — kept as a Record of literal Tailwind class
// strings (never built by interpolating the tier name into a class) so the
// JIT compiler can see every class at build time. VIP keeps its original
// amber/orange scheme; Premium and Supreme get their own so a buyer can
// tell at a glance which page they're on.
const TIER_THEME: Record<
  VIPTierName,
  {
    heroBg: string
    heroIcon: typeof Crown
    pillSelected: string
    cardSelected: string
    cardMutedSelected: string
    ctaGradient: string
    badgeBg: string
    chipSelected: string
    chipMuted: string
  }
> = {
  vip: {
    heroBg: 'bg-amber-400',
    heroIcon: Crown,
    pillSelected: 'bg-amber-400 text-white',
    cardSelected: 'border-amber-400 bg-amber-400 text-white',
    cardMutedSelected: 'text-amber-100',
    ctaGradient: 'from-amber-400 to-orange-500',
    badgeBg: 'bg-amber-500',
    chipSelected: 'bg-white/20 text-white',
    chipMuted: 'bg-amber-400/10 text-amber-600',
  },
  premium: {
    heroBg: 'bg-violet-500',
    heroIcon: Sparkles,
    pillSelected: 'bg-violet-500 text-white',
    cardSelected: 'border-violet-500 bg-violet-500 text-white',
    cardMutedSelected: 'text-violet-100',
    ctaGradient: 'from-violet-500 to-purple-600',
    badgeBg: 'bg-violet-600',
    chipSelected: 'bg-white/20 text-white',
    chipMuted: 'bg-violet-500/10 text-violet-600',
  },
  supreme: {
    heroBg: 'bg-rose-500',
    heroIcon: Star,
    pillSelected: 'bg-rose-500 text-white',
    cardSelected: 'border-rose-500 bg-rose-500 text-white',
    cardMutedSelected: 'text-rose-100',
    ctaGradient: 'from-rose-500 to-red-600',
    badgeBg: 'bg-rose-600',
    chipSelected: 'bg-white/20 text-white',
    chipMuted: 'bg-rose-500/10 text-rose-600',
  },
}

// Feature list per tier — icon + i18n key pairs. Lengths deliberately
// differ per tier (VIP has always shown exactly 3; Premium's extra perks
// earn a 4th line) — the render loop below maps over whatever's here rather
// than assuming a fixed count.
const TIER_FEATURES: Record<
  VIPTierName,
  { icon: typeof Heart; iconBg: string; iconColor: string; key: string }[]
> = {
  vip: [
    { icon: Heart, iconBg: 'bg-orange-400/20', iconColor: 'text-orange-500', key: 'vip.feature1' },
    { icon: Gem, iconBg: 'bg-cyan-500/20', iconColor: 'text-cyan-500', key: 'vip.feature2' },
    {
      icon: RewindIcon,
      iconBg: 'bg-amber-400/20',
      iconColor: 'text-amber-500',
      key: 'vip.feature3',
    },
  ],
  premium: [
    {
      icon: Heart,
      iconBg: 'bg-orange-400/20',
      iconColor: 'text-orange-500',
      key: 'vip.premium.feature1',
    },
    {
      icon: Gem,
      iconBg: 'bg-cyan-500/20',
      iconColor: 'text-cyan-500',
      key: 'vip.premium.feature2',
    },
    {
      icon: Crown,
      iconBg: 'bg-amber-400/20',
      iconColor: 'text-amber-500',
      key: 'vip.premium.feature3',
    },
    {
      icon: Search,
      iconBg: 'bg-pink-500/20',
      iconColor: 'text-pink-500',
      key: 'vip.premium.feature4',
    },
  ],
  supreme: [
    {
      icon: Star,
      iconBg: 'bg-rose-500/20',
      iconColor: 'text-rose-500',
      key: 'vip.supreme.feature1',
    },
  ],
}

export default function BuyVIPPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const reason = searchParams.get('reason')
  const urlTier = searchParams.get('tier')
  const [state, setState] = useState<PageState>({ status: 'loading' })
  const [now, setNow] = useState(() => Date.now())
  // Tracks whether the hosted QR image (see qris_image_url) failed to load —
  // unlike the old client-rendered SVG, this now depends on a network
  // fetch that can fail, so we need a fallback (see the awaiting_payment
  // render below).
  const [qrImgFailed, setQrImgFailed] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useTelegramBackButton(() => navigate('/profile'))

  // Picks the "reasonable middle" duration within a tier's own SKU list —
  // 90 days if present, else the first one.
  function defaultIndexFor(skus: VIPSku[]): number {
    const idx = skus.findIndex((s) => s.duration_days === 90)
    return idx >= 0 ? idx : 0
  }

  function loadSkus() {
    setState({ status: 'loading' })
    fetchVIPSkus()
      .then((skus) => {
        // ?tier= preselects a tier (e.g. ProfilePage's "Renew" CTA on an
        // active Premium pass links here with &tier=premium) — but only if
        // that tier actually has SKUs right now (withheld/kill-switched
        // tiers fall back to the first tier that does).
        const requestedTier = urlTier as VIPTierName | null
        const availableTiers = TIER_ORDER.filter((tr) => skus.some((s) => s.tier === tr))
        const tier =
          requestedTier && availableTiers.includes(requestedTier)
            ? requestedTier
            : (availableTiers[0] ?? 'vip')
        const tierSkus = skus.filter((s) => s.tier === tier)
        setState({ status: 'selecting', skus, tier, selected: defaultIndexFor(tierSkus) })
      })
      .catch((e) => {
        setState({
          status: 'error',
          message: e instanceof Error ? e.message : t('common.genericError'),
        })
      })
  }

  // On mount, check for an order left in-flight by a previous visit (see
  // pendingOrder.ts) before falling back to the normal tier picker. This is
  // what lets a user leave the Mini App to scan a QR in their banking app
  // and come back to the same awaiting-payment screen instead of starting
  // over from scratch.
  useEffect(() => {
    const pendingID = getPendingOrder('vip')
    if (pendingID == null) {
      loadSkus()
      return
    }
    setState({ status: 'loading' })
    fetchVIPOrder(pendingID)
      .then((fresh) => {
        if (fresh.status === 'pending') {
          setState({ status: 'awaiting_payment', order: fresh })
        } else if (fresh.status === 'completed') {
          clearPendingOrder('vip')
          setState({ status: 'completed', order: fresh })
        } else {
          clearPendingOrder('vip')
          setState({ status: 'expired', lastSku: lastSkuFrom(fresh) })
        }
      })
      .catch(() => {
        // Order no longer resolvable (deleted, network hiccup) — drop the
        // stale reference and fall back to the normal tier picker.
        clearPendingOrder('vip')
        loadSkus()
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (state.status !== 'awaiting_payment') return
    setQrImgFailed(false)
    tickRef.current = setInterval(() => setNow(Date.now()), 1000)
    return () => {
      if (tickRef.current) clearInterval(tickRef.current)
    }
  }, [state.status])

  useEffect(() => {
    if (state.status !== 'awaiting_payment') return
    const orderId = state.order.id

    function poll() {
      fetchVIPOrder(orderId)
        .then((fresh) => {
          if (fresh.status === 'completed') {
            hapticNotification('success')
            clearPendingOrder('vip')
            setState({ status: 'completed', order: fresh })
          } else if (fresh.status === 'expired' || fresh.status === 'failed') {
            clearPendingOrder('vip')
            setState({ status: 'expired', lastSku: lastSkuFrom(fresh) })
          }
          // 'pending' — keep polling, nothing to update.
        })
        .catch(() => {
          // Transient poll failure — the next tick tries again.
        })
    }

    // Poll faster while visible, slower (never zero) while hidden — see
    // BACKGROUND_POLL_INTERVAL_MS's doc comment above.
    function scheduleInterval() {
      if (pollRef.current) clearInterval(pollRef.current)
      const ms =
        document.visibilityState === 'visible' ? POLL_INTERVAL_MS : BACKGROUND_POLL_INTERVAL_MS
      pollRef.current = setInterval(poll, ms)
    }

    scheduleInterval()
    document.addEventListener('visibilitychange', scheduleInterval)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
      document.removeEventListener('visibilitychange', scheduleInterval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status])

  async function handleContinue(tier: VIPTierName, durationDays: number) {
    setState({ status: 'creating' })
    try {
      const order = await createVIPOrder(tier, durationDays)
      savePendingOrder('vip', order.id)
      setState({ status: 'awaiting_payment', order })
    } catch (e) {
      if (e instanceof ApiError && e.status === 502) {
        setState({ status: 'error', message: t('payment.gatewayError') })
      } else {
        setState({
          status: 'error',
          message: e instanceof Error ? e.message : t('common.genericError'),
        })
      }
    }
  }

  function headlineFor(tier: VIPTierName): string {
    if (reason === 'renew') {
      return tier === 'vip' ? t('vip.headlineRenew') : t(`vip.${tier}.headlineRenew`)
    }
    if (tier === 'vip') {
      if (reason === 'rewind') return t('vip.headlineRewind')
      if (reason === 'likes') return t('vip.headlineLikes')
      return t('vip.headlineDefault')
    }
    // "See Who Likes You" (docs/monetization-roadmap.md "Kejar Premium
    // Feature" #4) — reached from LikesPage's locked-tile CTA, Premium only.
    if (tier === 'premium' && reason === 'likes_you') return t('vip.premium.headlineLikesYou')
    return t(`vip.${tier}.headlineDefault`)
  }

  function completedTitleFor(tier: VIPTierName): string {
    return tier === 'vip' ? t('vip.completedTitle') : t(`vip.${tier}.completedTitle`)
  }

  return (
    <div className="flex min-h-dvh flex-col bg-tg-bg">
      <div className="flex items-center gap-3 px-4 pt-3">
        <button onClick={() => navigate('/profile')} className="text-sm font-medium text-tg-hint">
          ← {t('common.back')}
        </button>
      </div>

      {state.status === 'loading' && (
        <div className="flex flex-1 items-center justify-center text-tg-hint">
          {t('common.loading')}
        </div>
      )}

      {state.status === 'error' && (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
          <p className="text-sm text-red-500">{state.message}</p>
          <button
            onClick={loadSkus}
            className="rounded-xl bg-tg-secondary-bg px-6 py-3 font-medium text-tg-text"
          >
            {t('common.retry')}
          </button>
        </div>
      )}

      {state.status === 'selecting' &&
        (() => {
          const { skus, tier, selected } = state
          const availableTiers = TIER_ORDER.filter((tr) => skus.some((s) => s.tier === tr))
          const tierSkus = skus.filter((s) => s.tier === tier)
          const theme = TIER_THEME[tier]
          const HeroIcon = theme.heroIcon
          const weekly = tierSkus.find((s) => s.duration_days === 7)
          const baselinePerWeek = weekly ? perWeekPrice(weekly) : 0
          const selectedSku = tierSkus[selected]

          function selectTier(nextTier: VIPTierName) {
            const nextSkus = skus.filter((s) => s.tier === nextTier)
            setState({
              status: 'selecting',
              skus,
              tier: nextTier,
              selected: defaultIndexFor(nextSkus),
            })
          }

          return (
            <div className="flex flex-col gap-5 px-5 pb-8 pt-2">
              <div className="flex flex-col items-center gap-3 py-4">
                <div
                  className={`flex h-14 w-14 items-center justify-center rounded-full ${theme.heroBg}`}
                >
                  <HeroIcon className="h-7 w-7 fill-current text-white" />
                </div>
                <p className="text-center text-lg font-bold text-tg-text">{headlineFor(tier)}</p>
              </div>

              {/* Tier switcher — only rendered when more than one tier is
                  actually purchasable right now (see visibleVIPSKUs
                  server-side); a single-tier response (today's production
                  default, FEATURES_PREMIUM_ENABLED off) renders nothing
                  here, so the page looks exactly like it always has. */}
              {availableTiers.length > 1 && (
                <div className="flex justify-center gap-2">
                  {availableTiers.map((tr) => (
                    <button
                      key={tr}
                      onClick={() => selectTier(tr)}
                      aria-pressed={tr === tier}
                      className={`rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                        tr === tier
                          ? TIER_THEME[tr].pillSelected
                          : 'bg-tg-secondary-bg text-tg-text'
                      }`}
                    >
                      {t(`vip.tierNames.${tr}`)}
                    </button>
                  ))}
                </div>
              )}

              {/* Horizontally-swipeable duration cards */}
              <div className="-mx-5 flex snap-x snap-mandatory gap-3 overflow-x-auto px-5 pb-2">
                {tierSkus.map((sku, i) => {
                  const save = savePercent(sku, baselinePerWeek)
                  const isSelected = i === selected
                  return (
                    <button
                      key={sku.duration_days}
                      onClick={() => setState({ ...state, selected: i })}
                      className={`flex w-32 shrink-0 snap-start flex-col items-center gap-1 rounded-xl border-2 p-3 text-center ${
                        isSelected
                          ? theme.cardSelected
                          : 'border-tg-secondary-bg bg-tg-secondary-bg text-tg-text'
                      }`}
                    >
                      {save > 0 && (
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                            isSelected ? theme.chipSelected : theme.chipMuted
                          }`}
                        >
                          {t('vip.savePercent', { percent: save })}
                        </span>
                      )}
                      <span className="text-sm font-semibold">
                        {t(DURATION_LABEL_KEYS[sku.duration_days] ?? 'vip.durationWeek')}
                      </span>
                      <span className="text-sm font-bold">
                        {t('vip.perWeekPrice', { price: formatIDR(perWeekPrice(sku)) })}
                      </span>
                      {sku.duration_days !== 7 && weekly && (
                        <span
                          className={`text-[11px] line-through ${
                            isSelected ? theme.cardMutedSelected : 'text-tg-hint'
                          }`}
                        >
                          {t('vip.perWeekPrice', { price: formatIDR(baselinePerWeek) })}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>

              {/* Feature list */}
              <div className="rounded-xl border border-tg-secondary-bg p-4">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-tg-hint">
                  {t('vip.featuresTitle')}
                </p>
                <div className="flex flex-col gap-3">
                  {TIER_FEATURES[tier].map((feature) => {
                    const FeatureIcon = feature.icon
                    return (
                      <div key={feature.key} className="flex items-center gap-3">
                        <div
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${feature.iconBg}`}
                        >
                          <FeatureIcon className={`h-4 w-4 fill-current ${feature.iconColor}`} />
                        </div>
                        <span className="text-sm font-medium text-tg-text">{t(feature.key)}</span>
                      </div>
                    )
                  })}
                </div>
              </div>

              <button
                onClick={() => selectedSku && handleContinue(tier, selectedSku.duration_days)}
                disabled={!selectedSku}
                className={`relative w-full rounded-xl bg-gradient-to-r ${theme.ctaGradient} py-3.5 text-center font-bold text-white disabled:opacity-50`}
              >
                {t('vip.continue')}
                {selectedSku && (
                  <span
                    className={`absolute -top-2.5 right-3 rounded-full ${theme.badgeBg} px-2 py-0.5 text-[10px] font-bold text-white shadow`}
                  >
                    {t('vip.perDayPrice', {
                      price: formatIDR(selectedSku.price_idr / selectedSku.duration_days),
                    })}
                  </span>
                )}
              </button>

              <p className="text-center text-xs text-tg-hint">{t('vip.footerNote')}</p>
            </div>
          )
        })()}

      {state.status === 'creating' && (
        <div className="flex flex-1 items-center justify-center text-tg-hint">
          {t('payment.creatingOrder')}
        </div>
      )}

      {state.status === 'awaiting_payment' && (
        <div className="flex flex-1 flex-col items-center gap-4 px-6 py-6 text-center">
          <p className="text-base font-semibold text-tg-text">{t('payment.awaitingPayment')}</p>
          <p className="text-sm text-tg-hint">{t('payment.scanQris')}</p>
          {/* checkout_url is Saku Rupiah's own hosted invoice page — promoted
              to the primary CTA (not just a fallback for a failed QR image)
              because it handles the e-wallet hand-off itself: tapping it
              opens the OS's default browser, which can deep-link straight
              into the buyer's banking/e-wallet app, where downloading and
              re-scanning a QR image from a second device cannot. */}
          {state.order.checkout_url && (
            <button
              onClick={() => openExternalLink(state.order.checkout_url!)}
              className="w-full max-w-xs rounded-xl bg-tg-button px-4 py-3 text-sm font-semibold text-tg-button-text"
            >
              {t('payment.openPaymentPage')}
            </button>
          )}
          {state.order.qris_image_url && !qrImgFailed ? (
            <>
              {state.order.checkout_url && (
                <p className="text-xs text-tg-hint">{t('payment.or')}</p>
              )}
              <div className="rounded-2xl bg-white p-4">
                <img
                  src={state.order.qris_image_url}
                  alt={t('payment.qrAlt')}
                  width={220}
                  height={220}
                  onError={() => setQrImgFailed(true)}
                />
              </div>
              <button
                onClick={() =>
                  downloadFile(qrisDownloadURL(state.order.order_id), 'swipify-vip-qris.png')
                }
                className="flex items-center gap-2 rounded-xl bg-tg-secondary-bg px-4 py-2 text-sm font-medium text-tg-text"
              >
                <Download className="h-4 w-4" />
                {t('payment.downloadQr')}
              </button>
            </>
          ) : (
            qrImgFailed &&
            !state.order.checkout_url && (
              <p className="text-sm text-tg-hint">{t('payment.qrLoadError')}</p>
            )
          )}
          {state.order.total_payment_idr !== undefined && (
            <p className="text-lg font-bold text-tg-text">
              {formatIDR(state.order.total_payment_idr)}
            </p>
          )}
          {state.order.expires_at && (
            <p className="text-sm text-tg-hint">
              {t('payment.expiresIn', {
                time: formatCountdown(new Date(state.order.expires_at).getTime() - now),
              })}
            </p>
          )}
        </div>
      )}

      {state.status === 'completed' && (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
          <p className="text-xl font-semibold text-tg-text">
            {completedTitleFor(state.order.tier)}
          </p>
          <p className="text-sm text-tg-hint">
            {t('vip.completedMessage', {
              date: state.order.vip_expires_at ? formatDate(state.order.vip_expires_at) : '',
            })}
          </p>
          <button
            onClick={() => navigate('/profile')}
            className="rounded-xl bg-primary px-6 py-3 font-medium text-white"
          >
            {t('payment.backToProfile')}
          </button>
        </div>
      )}

      {state.status === 'expired' && (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
          <p className="text-xl font-semibold text-tg-text">{t('payment.expiredTitle')}</p>
          <p className="text-sm text-tg-hint">{t('payment.expiredMessage')}</p>
          {/* One-tap regenerate for the same SKU when we know it (see
              PageState's 'expired' variant) — skips sending the buyer back
              through the tier picker from scratch for what's usually just
              "I took longer than the payment window to pay." Falls back to
              the picker when we don't have a SKU to reuse (a stale
              localStorage order predating duration_days). */}
          <button
            onClick={() =>
              state.lastSku
                ? handleContinue(state.lastSku.tier, state.lastSku.durationDays)
                : loadSkus()
            }
            className="rounded-xl bg-tg-secondary-bg px-6 py-3 font-medium text-tg-text"
          >
            {t('payment.tryAgain')}
          </button>
        </div>
      )}
    </div>
  )
}
